import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Gear,
  MapPin,
  MagnifyingGlass,
  Timer,
  Lightning,
  Ambulance,
  Check,
  Truck,
} from "@phosphor-icons/react";
import { patchPreferences, setMainStation, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Rough mile-across viewport per Leaflet zoom level (mid-US latitudes).
const ZOOM_VIEWPORT_MI = {
  10: "~93 mi",
  11: "~46 mi",
  12: "~23 mi",
  13: "~6 mi",
  14: "~3 mi",
};

// Spawn-interval options (ms). Free tier gets 60s only; premium unlocks 30s and 15s.
const SPAWN_INTERVAL_OPTIONS = [
  { ms: 60000, label: "60s", sub: "Default", premiumOnly: false },
  { ms: 30000, label: "30s", sub: "Premium", premiumOnly: true },
  { ms: 15000, label: "15s", sub: "Premium", premiumOnly: true },
];

/**
 * SettingsDialog — player preferences.
 *
 * Sections:
 *   1. Main station picker (dropdown — compact)
 *   2. Initial map zoom slider
 *   3. Call spawn speed (premium speeds locked behind premium flag)
 *   4. Auto Transport (premium-only) — toggle + mode selector
 */
export default function SettingsDialog({
  open,
  onOpenChange,
  stations,
  mainStationId,
  onMainStationChanged,
  stationZoom,
  onStationZoomChanged,
  isPremium = false,
  preferences = {},
  onPreferencesUpdated,
}) {
  const [saving, setSaving] = useState(false);
  // Local zoom state for live preview while the slider is being dragged.
  const [localZoom, setLocalZoom] = useState(stationZoom ?? 13);
  const saveTimerRef = useRef(null);
  const [savingInterval, setSavingInterval] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);

  // Re-sync local state if parent updates the prop (e.g. dialog re-opened).
  useEffect(() => {
    if (open) setLocalZoom(stationZoom ?? 13);
  }, [open, stationZoom]);

  // ---- Main station change (now dropdown) ----
  const handleSetMain = useCallback(
    async (sid) => {
      if (sid === mainStationId || saving) return;
      setSaving(true);
      try {
        const updated = await setMainStation(sid);
        onMainStationChanged?.(updated.main_station_id);
        toast.success("Main station updated");
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not update");
      } finally {
        setSaving(false);
      }
    },
    [mainStationId, saving, onMainStationChanged]
  );

  // Live-preview the zoom on every drag; debounced server save on the latest value.
  const handleZoomChange = useCallback(
    (e) => {
      const v = Number(e.target.value);
      setLocalZoom(v);
      onStationZoomChanged?.(v);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        patchPreferences({ station_zoom: v }).catch((err) => {
          toast.error(formatApiError(err?.response?.data?.detail) || "Could not save zoom");
        });
      }, 400);
    },
    [onStationZoomChanged]
  );

  // Flush any pending save when the dialog closes so the user doesn't lose a tweak.
  useEffect(() => {
    if (!open && saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      const v = localZoom;
      patchPreferences({ station_zoom: v }).catch(() => {});
      saveTimerRef.current = null;
    }
  }, [open, localZoom]);

  // ---- Spawn interval change ----
  const handleSpawnInterval = useCallback(async (ms) => {
    if (savingInterval) return;
    if (preferences?.spawn_interval_ms === ms) return;
    setSavingInterval(true);
    try {
      const updated = await patchPreferences({ spawn_interval_ms: ms });
      onPreferencesUpdated?.(updated.preferences);
      toast.success(`Call spawn speed → ${ms / 1000}s`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update");
    } finally {
      setSavingInterval(false);
    }
  }, [preferences, savingInterval, onPreferencesUpdated]);

  // ---- Auto-transport toggle ----
  const autoEnabled = !!preferences?.auto_transport_enabled;
  const autoMode = preferences?.auto_transport_mode || "closest";

  const setAutoEnabled = useCallback(async (enabled) => {
    if (savingAuto) return;
    setSavingAuto(true);
    try {
      const updated = await patchPreferences({ auto_transport_enabled: enabled });
      onPreferencesUpdated?.(updated.preferences);
      toast.success(enabled ? "Auto Transport ON" : "Auto Transport OFF");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update");
    } finally {
      setSavingAuto(false);
    }
  }, [savingAuto, onPreferencesUpdated]);

  const setAutoMode = useCallback(async (mode) => {
    if (savingAuto) return;
    if (autoMode === mode) return;
    setSavingAuto(true);
    try {
      const updated = await patchPreferences({ auto_transport_mode: mode });
      onPreferencesUpdated?.(updated.preferences);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update");
    } finally {
      setSavingAuto(false);
    }
  }, [savingAuto, autoMode, onPreferencesUpdated]);

  // ---- Map icon style (CAD emblems) ----
  // Default is CAD ON. `undefined` in preferences means "never explicitly
  // set" — treat it as ON so first-time players see the emblems that were
  // spec'd as the default.
  const cadEnabled = preferences?.cad_icons_enabled !== false;
  const cadFullName = !!preferences?.cad_icons_show_full_name;
  const [savingCad, setSavingCad] = useState(false);
  const setCadEnabled = useCallback(async (enabled) => {
    if (savingCad) return;
    if (cadEnabled === enabled) return;
    setSavingCad(true);
    try {
      const updated = await patchPreferences({ cad_icons_enabled: enabled });
      onPreferencesUpdated?.(updated.preferences);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update");
    } finally {
      setSavingCad(false);
    }
  }, [savingCad, cadEnabled, onPreferencesUpdated]);
  const setCadFullName = useCallback(async (fullName) => {
    if (savingCad) return;
    if (cadFullName === fullName) return;
    setSavingCad(true);
    try {
      const updated = await patchPreferences({ cad_icons_show_full_name: fullName });
      onPreferencesUpdated?.(updated.preferences);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update");
    } finally {
      setSavingCad(false);
    }
  }, [savingCad, cadFullName, onPreferencesUpdated]);

  // ---- Auto-Clear Medics (premium) ----
  const autoClearMedics = !!preferences?.auto_clear_medics_enabled;
  const [savingAcm, setSavingAcm] = useState(false);
  const setAutoClearMedics = useCallback(async (enabled) => {
    if (savingAcm) return;
    if (autoClearMedics === enabled) return;
    setSavingAcm(true);
    try {
      const updated = await patchPreferences({ auto_clear_medics_enabled: enabled });
      onPreferencesUpdated?.(updated.preferences);
      toast.success(enabled ? "Auto-Clear Medics ON" : "Auto-Clear Medics OFF");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update");
    } finally {
      setSavingAcm(false);
    }
  }, [savingAcm, autoClearMedics, onPreferencesUpdated]);

  const currentInterval = preferences?.spawn_interval_ms ?? 60000;
  const currentMain = stations.find((s) => s.id === mainStationId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="settings-dialog"
        className="rounded-none border border-white/15 bg-[#0A0E14] text-white max-w-lg p-0"
      >
        <DialogHeader className="px-5 py-4 border-b border-white/10">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-zinc-300 flex items-center gap-2">
            <Gear size={14} /> Settings
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 py-4 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* ---------- Main station (dropdown) ---------- */}
          <section>
            <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
              <MapPin size={11} /> Main Station
            </h3>
            <p className="mono text-[10px] uppercase tracking-widest text-zinc-600 mb-3">
              The map zooms to this station every time you log in.
            </p>
            {stations.length === 0 ? (
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 border border-white/5 bg-white/[0.02] p-3">
                — build a station first —
              </div>
            ) : (
              <Select
                value={mainStationId || ""}
                onValueChange={handleSetMain}
                disabled={saving}
              >
                <SelectTrigger
                  data-testid="main-station-select"
                  className="rounded-none border-white/10 bg-black/40 mono text-[11px] tracking-wider focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500/60"
                >
                  <SelectValue
                    placeholder="Select a station"
                  >
                    {currentMain
                      ? `${currentMain.name} · ${currentMain.bays} bays`
                      : "Select a station"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent
                  className="rounded-none border-white/15 bg-[#0A0E14] text-white"
                >
                  {stations.map((s) => (
                    <SelectItem
                      key={s.id}
                      value={s.id}
                      data-testid={`main-station-option-${s.id}`}
                      className="rounded-none mono text-[11px] tracking-wider data-[highlighted]:bg-white/10 data-[highlighted]:text-emerald-200"
                    >
                      <span className="font-semibold">{s.name}</span>
                      <span className="text-zinc-500 ml-2">· {s.bays} bays</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </section>

          {/* ---------- Map zoom ---------- */}
          <section className="border-t border-white/5 pt-4">
            <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
              <MagnifyingGlass size={11} /> Initial Map Zoom
            </h3>
            <p className="mono text-[10px] uppercase tracking-widest text-zinc-600 mb-3">
              How tightly the map zooms to your main station on launch.
            </p>
            <div className="border border-white/10 bg-white/[0.02] p-3">
              <div className="flex items-baseline justify-between mb-2">
                <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Zoom Level
                </span>
                <span
                  data-testid="zoom-value"
                  className="mono text-sm font-bold text-emerald-300"
                >
                  {localZoom}
                  <span className="text-zinc-500 font-normal ml-2 text-[10px] uppercase tracking-widest">
                    {ZOOM_VIEWPORT_MI[localZoom] || ""}
                  </span>
                </span>
              </div>
              <input
                data-testid="zoom-slider"
                type="range"
                min={10}
                max={14}
                step={1}
                value={localZoom}
                onChange={handleZoomChange}
                className="w-full accent-emerald-500"
              />
              <div className="flex justify-between mt-1 mono text-[9px] uppercase tracking-widest text-zinc-600">
                <span>Wide</span>
                <span>City</span>
                <span>Street</span>
                <span>Block</span>
              </div>
            </div>
          </section>

          {/* ---------- Call spawn speed (moved from Profile) ---------- */}
          <section className="border-t border-white/5 pt-4">
            <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
              <Timer size={11} /> Call Spawn Speed
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {SPAWN_INTERVAL_OPTIONS.map((opt) => {
                const locked = opt.premiumOnly && !isPremium;
                const active = currentInterval === opt.ms;
                return (
                  <button
                    key={opt.ms}
                    type="button"
                    data-testid={`spawn-interval-${opt.ms}`}
                    disabled={locked || savingInterval}
                    onClick={() => handleSpawnInterval(opt.ms)}
                    className={`border p-3 text-left transition-colors ${
                      active
                        ? "border-emerald-400/60 bg-emerald-400/[0.08]"
                        : locked
                        ? "border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span
                        className={`mono font-bold text-base ${
                          active ? "text-emerald-300" : "text-zinc-200"
                        }`}
                      >
                        {opt.label}
                      </span>
                      {active && (
                        <Check size={12} weight="bold" className="text-emerald-400" />
                      )}
                    </div>
                    <div
                      className={`mono text-[9px] uppercase tracking-widest mt-1 flex items-center gap-1 ${
                        opt.premiumOnly ? "text-amber-300" : "text-zinc-600"
                      }`}
                    >
                      {opt.premiumOnly && <Lightning size={9} weight="fill" />}
                      {opt.sub}
                    </div>
                  </button>
                );
              })}
            </div>
            {!isPremium && (
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 mt-2">
                Faster speeds require Premium.
              </div>
            )}
          </section>

          {/* ---------- Map Icons (CAD emblems, free) ---------- */}
          <section className="border-t border-white/5 pt-4">
            <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
              <Truck size={11} /> Map Icons
            </h3>
            <p className="mono text-[10px] uppercase tracking-widest text-zinc-600 mb-3">
              CAD-style rectangle emblems colored by unit status. Off shows the truck artwork.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                type="button"
                data-testid="cad-icons-off"
                disabled={savingCad}
                onClick={() => setCadEnabled(false)}
                className={`border p-3 text-left transition-colors ${
                  !cadEnabled
                    ? "border-zinc-400/60 bg-zinc-400/[0.08]"
                    : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <span className={`mono font-bold text-base ${!cadEnabled ? "text-zinc-100" : "text-zinc-400"}`}>
                    ARTWORK
                  </span>
                  {!cadEnabled && <Check size={12} weight="bold" className="text-zinc-200" />}
                </div>
                <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                  Painted truck icons
                </div>
              </button>
              <button
                type="button"
                data-testid="cad-icons-on"
                disabled={savingCad}
                onClick={() => setCadEnabled(true)}
                className={`border p-3 text-left transition-colors ${
                  cadEnabled
                    ? "border-emerald-400/60 bg-emerald-400/[0.08]"
                    : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <span className={`mono font-bold text-base ${cadEnabled ? "text-emerald-300" : "text-zinc-400"}`}>
                    CAD
                  </span>
                  {cadEnabled && <Check size={12} weight="bold" className="text-emerald-400" />}
                </div>
                <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                  ENG / MED badges
                </div>
              </button>
            </div>

            {cadEnabled && (
              <div data-testid="cad-icons-label-row">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  Emblem label
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="cad-label-abbrev"
                    disabled={savingCad}
                    onClick={() => setCadFullName(false)}
                    className={`border p-3 text-left transition-colors ${
                      !cadFullName
                        ? "border-cyan-400/60 bg-cyan-400/[0.08]"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className={`mono font-bold text-sm ${!cadFullName ? "text-cyan-300" : "text-zinc-200"}`}>
                        ABBREVIATION
                      </span>
                      {!cadFullName && <Check size={12} weight="bold" className="text-cyan-400" />}
                    </div>
                    <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                      ENG · MED · LAD
                    </div>
                  </button>
                  <button
                    type="button"
                    data-testid="cad-label-fullname"
                    disabled={savingCad}
                    onClick={() => setCadFullName(true)}
                    className={`border p-3 text-left transition-colors ${
                      cadFullName
                        ? "border-pink-400/60 bg-pink-400/[0.08]"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className={`mono font-bold text-sm ${cadFullName ? "text-pink-300" : "text-zinc-200"}`}>
                        FULL NAME
                      </span>
                      {cadFullName && <Check size={12} weight="bold" className="text-pink-400" />}
                    </div>
                    <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                      e.g. Engine 1
                    </div>
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* ---------- Auto Transport (premium-only) ---------- */}
          <section className="border-t border-white/5 pt-4">
            <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
              <Ambulance size={11} /> Auto Transport
              <Lightning size={10} weight="fill" className="text-amber-300" />
            </h3>
            <p className="mono text-[10px] uppercase tracking-widest text-zinc-600 mb-3">
              Premium feature — Once a patient is assessed, the Medic is dispatched to a hospital automatically.
            </p>
            {!isPremium ? (
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 border border-amber-500/20 bg-amber-500/5 p-3">
                Premium membership required. Upgrade from Profile → Premium.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    type="button"
                    data-testid="auto-transport-off"
                    disabled={savingAuto}
                    onClick={() => setAutoEnabled(false)}
                    className={`border p-3 text-left transition-colors ${
                      !autoEnabled
                        ? "border-zinc-400/60 bg-zinc-400/[0.08]"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className={`mono font-bold text-base ${!autoEnabled ? "text-zinc-100" : "text-zinc-400"}`}>
                        OFF
                      </span>
                      {!autoEnabled && <Check size={12} weight="bold" className="text-zinc-200" />}
                    </div>
                    <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                      Manual transport
                    </div>
                  </button>
                  <button
                    type="button"
                    data-testid="auto-transport-on"
                    disabled={savingAuto}
                    onClick={() => setAutoEnabled(true)}
                    className={`border p-3 text-left transition-colors ${
                      autoEnabled
                        ? "border-emerald-400/60 bg-emerald-400/[0.08]"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className={`mono font-bold text-base ${autoEnabled ? "text-emerald-300" : "text-zinc-400"}`}>
                        ON
                      </span>
                      {autoEnabled && <Check size={12} weight="bold" className="text-emerald-400" />}
                    </div>
                    <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                      Auto-pick hospital
                    </div>
                  </button>
                </div>

                {autoEnabled && (
                  <div data-testid="auto-transport-mode-row">
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                      Hospital selection mode
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        data-testid="auto-transport-mode-closest"
                        disabled={savingAuto}
                        onClick={() => setAutoMode("closest")}
                        className={`border p-3 text-left transition-colors ${
                          autoMode === "closest"
                            ? "border-cyan-400/60 bg-cyan-400/[0.08]"
                            : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                        }`}
                      >
                        <div className="flex items-baseline justify-between">
                          <span className={`mono font-bold text-sm ${autoMode === "closest" ? "text-cyan-300" : "text-zinc-200"}`}>
                            CLOSEST
                          </span>
                          {autoMode === "closest" && <Check size={12} weight="bold" className="text-cyan-400" />}
                        </div>
                        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                          Nearest hospital (any)
                        </div>
                      </button>
                      <button
                        type="button"
                        data-testid="auto-transport-mode-capable"
                        disabled={savingAuto}
                        onClick={() => setAutoMode("capable")}
                        className={`border p-3 text-left transition-colors ${
                          autoMode === "capable"
                            ? "border-pink-400/60 bg-pink-400/[0.08]"
                            : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                        }`}
                      >
                        <div className="flex items-baseline justify-between">
                          <span className={`mono font-bold text-sm ${autoMode === "capable" ? "text-pink-300" : "text-zinc-200"}`}>
                            MOST CAPABLE
                          </span>
                          {autoMode === "capable" && <Check size={12} weight="bold" className="text-pink-400" />}
                        </div>
                        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                          Nearest w/ specialty
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          {/* ---------- Auto-Clear Medics (premium-only) ---------- */}
          <section className="border-t border-white/5 pt-4">
            <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
              <Ambulance size={11} /> Auto-Clear Medics
              <Lightning size={10} weight="fill" className="text-amber-300" />
            </h3>
            <p className="mono text-[10px] uppercase tracking-widest text-zinc-600 mb-3">
              Premium — once every patient is transported or refused, Medics still on scene auto-recall to station.
            </p>
            {!isPremium ? (
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 border border-amber-500/20 bg-amber-500/5 p-3">
                Premium membership required. Upgrade from Profile → Premium.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="auto-clear-medics-off"
                  disabled={savingAcm}
                  onClick={() => setAutoClearMedics(false)}
                  className={`border p-3 text-left transition-colors ${
                    !autoClearMedics
                      ? "border-zinc-400/60 bg-zinc-400/[0.08]"
                      : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-baseline justify-between">
                    <span className={`mono font-bold text-base ${!autoClearMedics ? "text-zinc-100" : "text-zinc-400"}`}>
                      OFF
                    </span>
                    {!autoClearMedics && <Check size={12} weight="bold" className="text-zinc-200" />}
                  </div>
                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                    Manual recall
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="auto-clear-medics-on"
                  disabled={savingAcm}
                  onClick={() => setAutoClearMedics(true)}
                  className={`border p-3 text-left transition-colors ${
                    autoClearMedics
                      ? "border-emerald-400/60 bg-emerald-400/[0.08]"
                      : "border-white/10 bg-white/[0.02] hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-baseline justify-between">
                    <span className={`mono font-bold text-base ${autoClearMedics ? "text-emerald-300" : "text-zinc-400"}`}>
                      ON
                    </span>
                    {autoClearMedics && <Check size={12} weight="bold" className="text-emerald-400" />}
                  </div>
                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                    Auto-recall when done
                  </div>
                </button>
              </div>
            )}
          </section>

          <div className="pt-2 flex justify-end">
            <Button
              data-testid="settings-close-btn"
              onClick={() => onOpenChange(false)}
              size="sm"
              variant="outline"
              className="rounded-none h-8 mono uppercase text-[10px] tracking-widest border-white/15 text-zinc-300 hover:bg-white/5"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
